import React from 'react';
import MainPrintLogo from './MainPrintLogo';
import {printPalette} from './printPalette';

interface RelatedCarInfo {
    relationshipType: string; // 'STC' or 'Attached'
    makeModel: string;
    vinSerial: string;
}

interface CarEntry {
    nr: number;
    description: string;
    vinSerial: string;
    destination: string;
    relatedCars?: RelatedCarInfo[];
}

export interface BookingConfirmationProps {
    bookingNumber?: string;
    terminal: string;
    date: string;
    pol: string;
    terminalAddress?: string;
    cars: CarEntry[];
    portRemarks?: string;
}

const BookingConfirmation: React.FC<BookingConfirmationProps> = ({
                                                                     bookingNumber,
                                                                     terminal,
                                                                     date,
                                                                     pol,
                                                                     terminalAddress,
                                                                     cars,
                                                                     portRemarks
                                                                 }) => {
    // Title format: "Booking confirmation" + POL + booking number
    const titleParts = ['Booking Confirmation'];
    if (pol) titleParts.push(pol);
    if (bookingNumber) titleParts.push(bookingNumber);
    const title = titleParts.join(' ');

    return (
        <div style={styles.container}>
            <MainPrintLogo/>

            <div style={styles.divider}/>

            <h2 style={styles.title}>{title}</h2>

            <div style={styles.infoSection}>
                <div style={styles.leftInfo}>
                    <p><span style={styles.label}>Terminal:</span> {terminal}</p>
                    <p><span style={styles.label}>Date:</span> {date}</p>
                    <p><span style={styles.label}>POL:</span> {pol}</p>
                </div>
                {terminalAddress && (
                    <div style={styles.rightInfo}>
                        {terminalAddress.split('\n').map((line, i) => (
                            <p key={i} style={styles.addressLine}>| {line}</p>
                        ))}
                    </div>
                )}
            </div>

            <table style={styles.table}>
                <thead>
                <tr>
                    <th style={{...styles.th, width: '8%'}}>Nr.</th>
                    <th style={{...styles.th, width: '32%'}}>Description</th>
                    <th style={{...styles.th, width: '30%'}}>VIN/Serial</th>
                    <th style={{...styles.th, width: '30%'}}>Destination</th>
                </tr>
                </thead>
                <tbody>
                {cars.map((car, index) => (
                    <tr key={index}>
                        <td style={{...styles.td, textAlign: 'center', fontWeight: 'bold'}}>{car.nr}</td>
                        <td style={{...styles.td, textAlign: 'center'}}>
                            <div>{car.description}</div>
                            {car.relatedCars && car.relatedCars.length > 0 && (
                                <div style={{marginTop: '4px'}}>
                                    {car.relatedCars.map((rc, rcIdx) => (
                                        <div key={rcIdx} style={{
                                            fontSize: '9px',
                                            color: printPalette.textMuted,
                                            fontStyle: 'italic'
                                        }}>
                                            ↳ {rc.relationshipType} {rc.makeModel}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </td>
                        <td style={{...styles.td, textAlign: 'center'}}>
                            <div>{car.vinSerial}</div>
                            {car.relatedCars && car.relatedCars.length > 0 && (
                                <div style={{marginTop: '4px'}}>
                                    {car.relatedCars.map((rc, rcIdx) => (
                                        <div key={rcIdx} style={{
                                            fontSize: '9px',
                                            color: printPalette.textMuted,
                                            fontStyle: 'italic'
                                        }}>
                                            ↳ {rc.vinSerial}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </td>
                        <td style={{...styles.td, textAlign: 'center'}}>{car.destination}</td>
                    </tr>
                ))}
                </tbody>
            </table>

            {portRemarks && (
                <div style={styles.remarksSection}>
                    <p style={styles.remarksLabel}>Port Remarks:</p>
                    <p style={styles.remarksText}>{portRemarks}</p>
                </div>
            )}
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: '11px',
        color: printPalette.text,
        padding: '20px',
        maxWidth: '800px',
        margin: '0 auto',
    },
    divider: {
        borderBottom: `1px solid ${printPalette.secondary}`,
        marginBottom: '20px',
    },
    title: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: printPalette.text,
        marginBottom: '20px',
    },
    infoSection: {
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '40px',
        marginBottom: '30px',
        fontSize: '11px',
    },
    leftInfo: {
        lineHeight: '1.8',
    },
    rightInfo: {
        textAlign: 'left',
        lineHeight: '1.8',
        color: printPalette.text,
        marginLeft: '300px',
    },
    addressLine: {
        margin: '0',
        padding: '0',
    },
    label: {
        display: 'inline-block',
        width: '60px',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: '40px',
    },
    th: {
        borderBottom: `2px solid ${printPalette.primaryDark}`,
        padding: '10px 8px',
        textAlign: 'center',
        fontWeight: 'bold',
        color: printPalette.primaryDark,
    },
    td: {
        padding: '12px 8px',
        borderBottom: `1px solid ${printPalette.borderLight}`,
        color: printPalette.text,
    },
    contactSection: {
        textAlign: 'center',
        marginTop: '40px',
        marginBottom: '20px',
    },
    contactText: {
        fontSize: '12px',
        color: printPalette.text,
    },
    remarksSection: {
        marginTop: '30px',
        padding: '12px',
        backgroundColor: printPalette.headerBg,
        borderRadius: '4px',
        textAlign: 'center',
    },
    remarksLabel: {
        fontWeight: 'bold',
        marginBottom: '4px',
        color: printPalette.primaryDark,
    },
    remarksText: {
        color: printPalette.text,
        whiteSpace: 'pre-wrap',
    },
};

export default BookingConfirmation;

